import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import Marquee from "react-fast-marquee";
import { ArrowUpRight, ArrowRight } from "lucide-react";
import { MaskReveal, Reveal, Overline } from "@/components/Motion";
import { ExhibitionPresence } from "@/components/ExhibitionPresence";
import { WhoWeAre } from "@/components/WhoWeAre";
import { TrustedExperience } from "@/components/TrustedExperience";
import { MarbleBreak } from "@/components/MarbleBreak";
import { SEO } from "@/components/SEO";
import { ASSETS, SERVICES } from "@/lib/assets";

const HERO_LINES = ["Premium exhibition", "environments. Italian", "craftsmanship."];

const Home = () => {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const overlayOpacity = useTransform(scrollYProgress, [0, 1], [0.55, 0.85]);

  return (
    <div data-testid="home-page">
      <SEO title="EVO Exhibitions | Exhibition Stand Design & Build in Dubai & the Middle East" description="Premium exhibition stand design, build and turnkey delivery across Dubai, the UAE and the Middle East — Italian craftsmanship, global impact." path="/" />
      {/* HERO */}
      <section ref={heroRef} className="relative h-screen min-h-[680px] flex items-end overflow-hidden">
        <motion.div style={{ y: bgY }} className="absolute inset-0 will-change-transform">
          {ASSETS.heroVideo ? (
            <video
              autoPlay muted loop playsInline
              poster={ASSETS.cistern}
              className="absolute inset-0 w-full h-full object-cover evo-kenburns"
            >
              <source src={ASSETS.heroVideo} type="video/mp4" />
            </video>
          ) : (
            <>
              <img src={ASSETS.cistern} alt="Cinematic exhibition environment" className="absolute inset-0 w-full h-full object-cover evo-kenburns evo-layer-a" />
              <img src={ASSETS.curvedMarble} alt="Italian architectural interior" className="absolute inset-0 w-full h-full object-cover evo-kenburns evo-layer-b" />
            </>
          )}
        </motion.div>
        <motion.div style={{ opacity: overlayOpacity }} className="absolute inset-0 bg-evo-black" />
        <div className="absolute inset-0 bg-gradient-to-t from-evo-black via-evo-black/20 to-evo-black/40" />

        <div className="relative z-10 px-6 md:px-12 lg:px-20 pb-20 md:pb-28 w-full">
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }}>
            <Overline className="block mb-8">Global Exhibition · Design · Build · Deliver</Overline>
          </motion.div>
          <h1 className="font-serif font-light text-5xl sm:text-6xl lg:text-8xl leading-[0.95] tracking-tight text-evo-pearl max-w-5xl">
            {HERO_LINES.map((line, i) => (
              <MaskReveal key={i} delay={0.35 + i * 0.15}>
                {line}
              </MaskReveal>
            ))}
          </h1>
          <motion.div
            className="mt-10 flex flex-col sm:flex-row sm:items-center gap-6"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.1, duration: 0.8 }}
          >
            <Link
              to="/contact"
              data-testid="hero-quote-btn"
              className="inline-flex items-center gap-3 bg-evo-gold text-evo-black hover:bg-evo-goldsoft transition-colors duration-300 px-9 py-5 text-xs uppercase tracking-[0.2em] group"
            >
              Request a Quotation
              <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
            <Link to="/experiences" data-testid="hero-work-link" className="link-underline text-sm uppercase tracking-[0.2em] text-evo-pearl/80 hover:text-evo-pearl">
              View Selected Experiences
            </Link>
          </motion.div>
        </div>
      </section>

      {/* MARQUEE */}
      <section className="py-8 border-y border-white/5 bg-evo-marble1 overflow-hidden">
        <Marquee speed={40} gradient={false} autoFill>
          {["Global Exhibition Design", "Custom Build", "Turnkey Delivery", "National Pavilions", "Italian Craftsmanship"].map((t, i) => (
            <span key={i} className="font-serif italic text-4xl md:text-5xl text-evo-pearl/25 mx-16">
              {t} <span className="text-evo-gold/40 not-italic mx-4">✦</span>
            </span>
          ))}
        </Marquee>
      </section>

      {/* INTRO / MANIFESTO TEASER */}
      <section className="px-6 md:px-12 lg:px-20 py-28 md:py-40">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-3">
            <Reveal><Overline>The Studio</Overline></Reveal>
          </div>
          <div className="lg:col-span-8">
            <Reveal delay={0.1}>
              <p className="font-serif font-light text-3xl md:text-4xl lg:text-5xl leading-[1.15] tracking-tight text-evo-pearl text-balance">
                We design and build exhibition environments where <span className="text-evo-gold italic">Italian craftsmanship</span> meets Middle East ambition — spaces engineered to command attention and convert it into opportunity.
              </p>
            </Reveal>
            <Reveal delay={0.2}>
              <Link to="/about" data-testid="intro-about-link" className="mt-10 inline-flex items-center gap-2 text-evo-gold link-underline text-sm uppercase tracking-[0.2em] group">
                Our Philosophy <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
            </Reveal>
          </div>
        </div>
      </section>

      {/* WHO WE ARE COLLAGE */}
      <WhoWeAre />

      {/* PREMIUM MARBLE BREAK */}
      <MarbleBreak />

      {/* TRUSTED EXPERIENCE */}
      <TrustedExperience />

      {/* SERVICES TEASER — asymmetric bento */}
      <section className="px-6 md:px-12 lg:px-20 pb-28 md:pb-40">
        <Reveal className="flex items-end justify-between mb-14 flex-wrap gap-6">
          <div>
            <Overline className="block mb-4">Capabilities</Overline>
            <h2 className="font-serif font-light text-4xl md:text-5xl tracking-tight text-evo-pearl">What we deliver</h2>
          </div>
          <Link to="/services" data-testid="services-all-link" className="link-underline text-sm uppercase tracking-[0.2em] text-evo-pearl/70 hover:text-evo-gold">
            All 10 Services
          </Link>
        </Reveal>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
          {[SERVICES[0], SERVICES[1], SERVICES[3], SERVICES[2]].map((s, i) => (
            <Reveal key={s.n} delay={i * 0.08} className={i === 0 ? "md:col-span-7" : i === 1 ? "md:col-span-5" : i === 2 ? "md:col-span-5" : "md:col-span-7"}>
              <Link
                to="/services"
                data-testid={`service-card-${s.n}`}
                className="group block h-full bg-evo-marble1 border border-white/5 hover:border-evo-gold/30 transition-colors duration-500 p-8 md:p-10"
              >
                <div className="flex items-start justify-between">
                  <span className="font-serif text-evo-gold/70 text-lg">{s.n}</span>
                  <ArrowUpRight size={20} className="text-evo-ash/40 group-hover:text-evo-gold transition-all duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </div>
                <h3 className="font-serif text-2xl md:text-3xl text-evo-pearl mt-16 mb-3 group-hover:text-evo-gold transition-colors duration-300">{s.title}</h3>
                <p className="text-evo-ash text-sm leading-relaxed max-w-md font-light">{s.desc}</p>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      {/* GLOBAL REACH */}
      <section className="relative overflow-hidden border-y border-white/5">
        <div className="grid grid-cols-1 lg:grid-cols-2">
          <div className="relative h-[380px] lg:h-auto overflow-hidden group">
            <img src={ASSETS.worldMap} alt="Global exhibition reach" className="w-full h-full object-cover transition-transform duration-[1.2s] group-hover:scale-105" />
            <div className="absolute inset-0 bg-evo-black/30" />
          </div>
          <div className="px-6 md:px-12 lg:px-20 py-20 lg:py-32 flex flex-col justify-center bg-evo-marble1">
            <Reveal><Overline className="block mb-6">Global Impact</Overline></Reveal>
            <Reveal delay={0.1}>
              <h2 className="font-serif font-light text-4xl md:text-5xl lg:text-6xl leading-tight tracking-tight text-evo-pearl mb-8">
                One partner,<br />every show floor.
              </h2>
            </Reveal>
            <Reveal delay={0.15}>
              <p className="text-evo-ash leading-relaxed max-w-md font-light mb-12">
                Creating exceptional exhibition experiences across the Middle East — we deliver turnkey stands and national pavilions with a single accountable team from concept to dismantle.
              </p>
            </Reveal>
            <div className="grid grid-cols-3 gap-6">
              {[["01","Design"],["02","Build"],["03","Deliver"]].map(([num, label], i) => (
                <Reveal key={label} delay={0.2 + i * 0.08}>
                  <p className="font-serif text-3xl md:text-4xl text-evo-gold">{num}</p>
                  <p className="text-xs uppercase tracking-[0.15em] text-evo-pearl/80 mt-2">{label}</p>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* EXHIBITION PRESENCE */}
      <ExhibitionPresence />

      {/* CTA BAND */}
      <section className="relative overflow-hidden">
        <img src={ASSETS.marble} alt="" className="absolute inset-0 w-full h-full object-cover opacity-50" />
        <div className="absolute inset-0 bg-evo-black/70" />
        <div className="relative z-10 px-6 md:px-12 lg:px-20 py-28 md:py-40 text-center">
          <Reveal>
            <h2 className="font-serif font-light text-4xl md:text-6xl lg:text-7xl tracking-tight text-evo-pearl max-w-4xl mx-auto text-balance">
              Ready to design your next <span className="text-evo-gold italic">stand</span>?
            </h2>
          </Reveal>
          <Reveal delay={0.15}>
            <Link
              to="/contact"
              data-testid="cta-quote-btn"
              className="mt-12 inline-flex items-center gap-3 bg-evo-gold text-evo-black hover:bg-evo-goldsoft transition-colors duration-300 px-10 py-5 text-xs uppercase tracking-[0.2em] group"
            >
              Start Your Enquiry
              <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default Home;
